/**
 * Sprint T-17.1 · Notification Center · PreferencesTab.
 * Solo /me · superadmin edita sus preferences.
 * §4.5 · hybrid model (transactional bypass · normal respect · marketing opt-in).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Ban } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useMyPreferences, useUpdateMyPreferences } from "../../hooks/useNotifications";
import ChannelIcon, { ALL_CHANNELS } from "./shared/ChannelIcon";
import { UnsubscribeConfirmDialog } from "./shared/Dialogs";

const AVAILABLE_TOPICS = [
  "security_alert", "security_failures_digest", "automation_job_failed",
  "appointment_reminder", "appointment_review_request", "event_generic",
  "product_update",
];
const MARKETING_TOPICS = ["marketing", "product_update"];

function Toggle({ label, checked, onChange, testId, disabled = false }) {
  return (
    <label className={`flex items-center justify-between rounded border border-[var(--ag-border)] px-3 py-2 ${disabled ? "opacity-50" : ""}`}>
      <span className="text-xs">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        data-testid={testId}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-emerald-500" : "bg-slate-500/40"
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
    </label>
  );
}

function ChipMulti({ items, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={`${testIdPrefix}-chips`}>
      {items.map((t) => {
        const on = selected.includes(t);
        return (
          <button
            key={t}
            type="button"
            data-testid={`${testIdPrefix}-${t}`}
            onClick={() => onToggle(t)}
            className={`rounded border px-2 py-0.5 font-mono text-[10px] transition-colors ${
              on
                ? "border-[var(--ag-gold)]/60 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]"
                : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/40"
            }`}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}

export default function PreferencesTab() {
  const q = useMyPreferences();
  const update = useUpdateMyPreferences();
  const [draft, setDraft] = useState(null);
  const [unsubDialog, setUnsubDialog] = useState(false);

  useEffect(() => {
    if (q.data && !draft) {
      setDraft({
        channels: q.data.channels || {},
        topics_disabled: q.data.topics_disabled || [],
        topics_opted_in: q.data.topics_opted_in || [],
        unsubscribed_globally: Boolean(q.data.unsubscribed_globally),
        locale: q.data.locale || "es-ES",
      });
    }
  }, [q.data, draft]);

  const save = async () => {
    try {
      await update.mutateAsync(draft);
      toast.success("Preferencias guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar");
    }
  };

  const confirmUnsub = async () => {
    try {
      await update.mutateAsync({ ...draft, unsubscribed_globally: true });
      setDraft((d) => ({ ...d, unsubscribed_globally: true }));
      setUnsubDialog(false);
      toast.success("Suscripción global cancelada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cancelar");
    }
  };

  return (
    <section className="space-y-4" data-testid="notif-preferences-tab">
      <div
        data-testid="notif-preferences-pedagogical-banner"
        className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 text-xs"
      >
        <div className="font-semibold text-indigo-400">Editas tus propias preferencias como superadmin.</div>
        <ul className="mt-1.5 list-disc pl-5 text-[10px] text-[var(--ag-text-dim)]">
          <li><strong>Transaccional</strong> (verificación · seguridad · reset password): siempre se envía salvo channel opt-out.</li>
          <li><strong>Normal</strong> (informativo): respeta todas las preferencias.</li>
          <li><strong>Marketing</strong>: requiere opt-in explícito en Topics opted-in.</li>
        </ul>
      </div>

      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudieron cargar tus preferencias" testId="notif-preferences-error" />}

      {draft && (
        <>
          <WidgetCard
            title="Channels"
            description="Activa o desactiva por canal · aplica a todas las notificaciones"
            citations={["GET/PUT /api/notifications/preferences/me"]}
            testId="notif-prefs-channels"
          >
            <WidgetGrid cols="md:grid-cols-3 lg:grid-cols-5">
              {ALL_CHANNELS.map((ch) => (
                <Toggle
                  key={ch}
                  label={
                    <span className="inline-flex items-center gap-2">
                      <ChannelIcon channel={ch} size={12} />
                      <span className="font-mono uppercase text-[10px]">{ch}</span>
                    </span>
                  }
                  checked={draft.channels[ch] ?? false}
                  onChange={(v) => setDraft((d) => ({ ...d, channels: { ...d.channels, [ch]: v } }))}
                  testId={`notif-prefs-channel-${ch}`}
                />
              ))}
            </WidgetGrid>
          </WidgetCard>

          <WidgetCard
            title="Topics disabled"
            description="Topics NORMAL que rechazas (marketing NO cuenta aquí)"
            testId="notif-prefs-topics-disabled"
          >
            <ChipMulti
              items={AVAILABLE_TOPICS.filter((t) => !MARKETING_TOPICS.includes(t))}
              selected={draft.topics_disabled}
              onToggle={(t) => setDraft((d) => ({
                ...d,
                topics_disabled: d.topics_disabled.includes(t)
                  ? d.topics_disabled.filter((x) => x !== t)
                  : [...d.topics_disabled, t],
              }))}
              testIdPrefix="notif-prefs-topic-disabled"
            />
          </WidgetCard>

          <WidgetCard
            title="Topics opted-in (marketing)"
            description="Marketing requiere opt-in explícito por topic"
            testId="notif-prefs-topics-opted-in"
          >
            <ChipMulti
              items={MARKETING_TOPICS}
              selected={draft.topics_opted_in}
              onToggle={(t) => setDraft((d) => ({
                ...d,
                topics_opted_in: d.topics_opted_in.includes(t)
                  ? d.topics_opted_in.filter((x) => x !== t)
                  : [...d.topics_opted_in, t],
              }))}
              testIdPrefix="notif-prefs-topic-opt-in"
            />
          </WidgetCard>

          <WidgetCard title="Locale" testId="notif-prefs-locale-card">
            <select
              data-testid="notif-prefs-locale"
              value={draft.locale}
              onChange={(e) => setDraft((d) => ({ ...d, locale: e.target.value }))}
              className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 text-xs"
            >
              <option value="es-ES">es-ES</option>
              <option value="en-US">en-US</option>
            </select>
          </WidgetCard>

          <WidgetCard
            title="Cancelar suscripción global"
            description="Estado destructivo · solo se seguirán entregando notificaciones transaccionales críticas"
            testId="notif-prefs-unsub-card"
          >
            {draft.unsubscribed_globally ? (
              <div
                data-testid="notif-prefs-unsub-status-active"
                className="rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-500"
              >
                <Ban className="mr-1 inline h-3 w-3" />
                Estás globalmente unsubscribed. Puedes revertirlo desmarcando y guardando.
                <div className="mt-2">
                  <button
                    type="button"
                    data-testid="notif-prefs-unsub-revert"
                    onClick={() => setDraft((d) => ({ ...d, unsubscribed_globally: false }))}
                    className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-500 hover:bg-emerald-500/20"
                  >
                    Reactivar suscripción
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="notif-prefs-unsub-btn"
                onClick={() => setUnsubDialog(true)}
                className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-500 hover:bg-red-500/20"
              >
                <Ban className="h-3 w-3" /> Cancelar suscripción global
              </button>
            )}
          </WidgetCard>

          <div className="flex items-center justify-end gap-2 border-t border-[var(--ag-border)] pt-3">
            <button
              type="button"
              data-testid="notif-prefs-save"
              disabled={update.isPending}
              onClick={save}
              className="inline-flex items-center gap-1 rounded bg-[var(--ag-gold)] px-3 py-1.5 text-xs font-medium text-black hover:opacity-90 disabled:opacity-50"
            >
              <Save className="h-3 w-3" /> {update.isPending ? "Guardando…" : "Guardar preferencias"}
            </button>
          </div>
        </>
      )}

      {unsubDialog && (
        <UnsubscribeConfirmDialog
          onClose={() => setUnsubDialog(false)}
          onConfirm={confirmUnsub}
          pending={update.isPending}
        />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["§17.4 hybrid preferences · transactional bypass documented"]} />
      </footer>
    </section>
  );
}
